import { useViewport } from '../ViewportProvider';
import { useThemeTokens } from '../ThemeProvider';
import { resolveResponsiveProp } from './useResponsiveProp';
/**
 * @typedef {import('@telus-uds/system-constants/viewports').Viewport} Viewport
 * @typedef {import('./props/spacingProps.js').SpacingValue} SpacingValue
 * @typedef {import('./props/spacingProps.js').SpacingIndex} SpacingIndex
 * @typedef {import('./props/spacingProps.js').SpacingObject} SpacingObject
 * @typedef {import('./props/spacingProps.js').SpacingOptions} SpacingOptions
 */

const resolveSpacingOptions = space => {
  if (!space?.options) return {};
  const {
    size,
    variant,
    subtract = 0
  } = space.options;
  const overridden = typeof size === 'number';

  // Might need an option that adapts the size value by current user's system font scale, so that
  // meaningful spacing between items doesn't disappear on the highest a11y font scale settings.
  // https://github.com/telus/universal-design-system/issues/583

  return {
    tokens: {
      size
    },
    variant,
    overridden,
    subtract
  };
};

/**
 * Pass a {@link SpacingValue}, which is one of:
 *
 * - A number 0-11 ({@link SpacingIndex}) pointing to an index on the theme's spacing scale
 * - Or, an object ({@link SpacingObject}), with optional properties:
 *   -  `xs`, `sm`, `md`, `lg`, `xl`: {@link SpacingIndex} to apply at or above these specified {@link Viewport}
 *   - `options`: an optional {@link SpacingOptions} object, see below
 *   - `space`: a {@link SpacingIndex} to apply to all viewports (can be used alongside `options`)
 *
 * ## Example
 *
 * If the theme's spacing scale is `[0, 4, 8, 12, 16, 24, 48]` with no theme rules:
 *
 * - `useSpacingScale(0)` returns `0`
 * - `useSpacingScale(2)` returns `8`
 * - `useSpacingScale({ xs: 3, lg: 4 })` returns `12` at 'xs', 'sm' or 'md' viewports, and `16` at 'lg' or 'xl'.
 *
 * These viewport properties are intended to support case-specific responsive layout changes, for example, where a
 * grid item drops or adds spacing on a particular side at viewports where the parent changes the layout shape.
 *
 * ## Theming
 *
 * A theme's `'spacingScale'` has theme rules and appearances same as components, and may support `viewport`.
 * For example, a theme with the following rule would change index [2] above from `8` to `12` on large viewports:
 *
 * ```json
 * { if: { space: 2, viewport: ['lg', 'xl'] }, tokens: { size: 12 }}
 * ```
 *
 * Setting responsive spacing in the theme is the preferred way to adapt the aesthetic tightness or looseness of
 * a theme to the available space without changing the shape of the layout itself.
 *
 * ## Options
 *
 * Space values passed as objects may have an `options` key, with the following optional properties:
 *
 * - `variant`: Themes may choose to have spacing scale variants, same as variants in component themes.
 *   For example, if a theme rule contains `{ if: { space: 2, compact: true }, tokens: { size: 6 }}`,
 *   this tighter spacing can be accessed with:
 *
 * ```jsx
 * const compactSize = useSpacingScale({ space: 2, options: { variant: { compact: true }}})`
 * ```
 *
 * - `subtract`: Sometimes on-brand spacing needs to be reduced by another value to achieve an on-brand result.
 *   For example, a component with a variable border may want to subtract its border width from its padding so
 *   the total distance from content edge to bounding box is a valid theme value, regardless of border width:
 *
 * ```jsx
 * const padding = useSpacingScale({ space: 2, options: { subtract: themeTokens.borderWidth }})
 * ```
 *
 * - `size`: In exceptional cases, the theme's spacing scale may be bypassed by passing a `size` option.
 *   This can result in layouts that may be rejected for being off-brand so should only be used as a
 *   last resort for fixing layout problems (e.g. when working around non-branded embedded content).
 *   Where possible, fixing layout issues using a spacing scale value and the `subtract` option is preferred.
 *
 * ```jsx
 * // Comments should be included when `size` is used, stating why this off-brand size is needed
 * const iframeOffset = useSpacingScale({ options: { size: 13 }})`
 * ```
 *
 * ## References
 *
 * `/ADRs/inter-component-spacing/README.md` - ADR on this structure
 *
 * @param {SpacingValue} [spaceValue] - a {@link SpacingIndex} number, or a {@link SpacingObject}
 * @returns {number}
 */
const useSpacingScale = spaceValue => {
  // In future, may need to consider window height as well as width, particularly for native apps,
  // e.g. to ensure designs don't look lost on large, tall, not-so-wide portrait screens.
  const viewport = useViewport();
  const {
    tokens,
    variant,
    overridden,
    subtract = 0
  } = resolveSpacingOptions(spaceValue);
  const space = !overridden && (spaceValue?.space ?? resolveResponsiveProp(spaceValue, viewport, 0));
  const {
    size
  } = useThemeTokens('spacingScale', tokens, variant, {
    space: typeof space === 'number' ? space : 0,
    viewport
  });
  return Math.max(size - subtract, 0);
};
export default useSpacingScale;